import React, { useState } from "react";
import { useIntl } from "react-intl";
import cn from "classnames";

// Mui
import {
    Box,
    Typography,
    TableRow,
    TableCell,
    IconButton,
    Tooltip,
    Collapse,
    Button,
    LinearProgress,
    ClickAwayListener,
    IconButtonProps,
} from "@mui/material";

// Icons
import icons from "enums/icons";
import InfoIcon from "@mui/icons-material/Info";
import RestoreIcon from "@mui/icons-material/Restore";

// Utils
import { useAuth } from "utils/hooks";
import { formatDistanceToNow } from "date-fns";
import {
    AssessmentCentrePaceStatus,
    ConvertedAssessmentCentreUserData,
} from "pages/AssessmentCentre/adapters/converters";

// Styles
import UserTableRowStyles from "./UserTableRow.styles";

// Components
import { TeamChips } from "../TeamChips";
import CustomIcon from "components/CustomIcon";
import { InfoWithIcon } from "pages/ClientHub/components";
import NavButton from "components/HeaderNavigation/NavButton";
import PersonWorkHistory from "components/PersonWorkHistory";

// Types
import { paceStatusIcon } from "enums/paceStatus";

const UserTableRow = ({
    data,
    Checkbox,
    handleShowReport,
    handleDownloadReport,
    handleTransferProfile,
    handleGetInfo,
    handleSendReminder,
}: {
    data: ConvertedAssessmentCentreUserData;
    Checkbox: React.ReactNode;
    handleShowReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleDownloadReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleTransferProfile: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleGetInfo: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleSendReminder: (userId: number) => void;
}) => {
    const intl = useIntl();
    const classes = UserTableRowStyles();

    const { loggedInUserData } = useAuth();

    const [shouldExpand, setShouldExpand] = useState(false);
    const [openPaceInfo, setOpenPaceInfo] = useState(false);
    const [toggleShowWorkHistory, setToggleShowWorkHistory] = useState(false);
    const [WHAnchorEl, setWHAnchorEl] = useState<HTMLElement | null>(null);

    const hasLinkedin = Boolean(data.linkedinId);
    const isLoggedIdInUser = data.id === loggedInUserData.userId;
    const progress = data.assessments?.most_recent?.progress?.meta?.meter?.percentage || 0;
    const shouldShowReminderOption = data.paceStatus === "Incomplete" && !isLoggedIdInUser;

    const handleToggleExpand = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleStopPropagation(event);
        setShouldExpand(!shouldExpand);
    };

    const handleTogglePaceInfo = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleStopPropagation(event);
        setOpenPaceInfo(!openPaceInfo);
    };

    const handleStopPropagation = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
    };

    const onClickRestoreIcon = (event: React.MouseEvent<HTMLElement>) => {
        if (!data?.personId) return;

        setToggleShowWorkHistory(true);
        setWHAnchorEl(event.currentTarget);
    };

    const onCloseWH = () => {
        setToggleShowWorkHistory(false);
        setWHAnchorEl(null);
    };

    const PaceInfo = () => {
        return (
            <Box onClick={handleStopPropagation} className={classes.paceInfoWrapper}>
                <Box className={classes.paceInfoInner}>
                    <Typography className={classes.paceInfoDescription}>
                        {intl.formatMessage({ id: "userManagement.sharePace.tooltip.content" })}
                    </Typography>
                    <IconButton className={classes.paceInfoCloseButton} onClick={handleTogglePaceInfo}>
                        <CustomIcon icon={icons.close} />
                    </IconButton>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                    <Button className={classes.paceInfoRequestDataButton} onClick={() => console.log("here")}>
                        {intl.formatMessage({ id: "userManagement.sharePace.tooltip.buttonLabel" })}
                    </Button>
                </Box>
            </Box>
        );
    };

    const PaceStatus = ({ status }: { status: AssessmentCentrePaceStatus }) => {
        const PaceIconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function PaceIconButton(
            props,
            ref
        ) {
            if (status === "Not Sharing") {
                return (
                    // <IconButton {...props} ref={ref} onClick={handleTogglePaceInfo} sx={{ pl: 0 }}>
                    <CustomIcon icon={icons[paceStatusIcon[status]]} sx={{ fontSize: 14, mr: 1 }} />
                    // </IconButton>
                );
            }
            return <CustomIcon icon={icons[paceStatusIcon[status]]} sx={{ fontSize: 14, mr: 1 }} />;
        });

        if (status === "Not Sharing") {
            return (
                <InfoWithIcon
                    startSlot={
                        <Box display="flex" alignItems="center">
                            <Tooltip
                                arrow
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                                open={openPaceInfo}
                                enterDelay={500}
                                leaveDelay={200}
                                title={<PaceInfo />}
                                classes={{ popper: classes.paceInfoPopper }}
                            >
                                <PaceIconButton />
                            </Tooltip>
                        </Box>
                    }
                    entityName={data.userName}
                />
            );
        }

        return (
            <Box display="flex" alignItems="center">
                {status !== "Unknown" && <PaceIconButton />}
                <Box>{data.userName}</Box>
            </Box>
        );
    };

    return (
        <>
            <TableRow className={classes.row} onClick={(event) => handleToggleExpand(event)}>
                <TableCell>
                    <PaceStatus status={data.paceStatus} />
                </TableCell>
                <TableCell>
                    <LinearProgress
                        value={progress}
                        variant="determinate"
                        className={cn([classes.progressBar], {
                            [classes.progressBarComplete]: progress > 99,
                        })}
                    />
                </TableCell>
                <TableCell>{Boolean(data.teams.length) && <TeamChips chips={data.teams} />}</TableCell>
                <TableCell>
                    {data.lastContactedOn !== null
                        ? formatDistanceToNow(new Date(data.lastContactedOn), { addSuffix: true })
                        : ""}
                </TableCell>
                <TableCell>
                    {shouldShowReminderOption && (
                        <NavButton
                            className={classes.reminderButton}
                            icon={null}
                            size="small"
                            variant="outlined"
                            onClick={(ev) => {
                                ev.stopPropagation();
                                handleSendReminder(data.id);
                            }}
                        >
                            Send PACE Reminder
                        </NavButton>
                    )}
                </TableCell>
                <TableCell>{shouldShowReminderOption && Checkbox}</TableCell>
                <TableCell>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                        {data.paceStatus !== "Incomplete" && (
                            <>
                                {data.paceStatus === "Completed" && (
                                    <>
                                        <Tooltip
                                            arrow
                                            placement="bottom"
                                            title={intl.formatMessage({ id: "pace.admin.cardIcon.tooltip" })}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                                    handleShowReport(ev, data.id)
                                                }
                                            >
                                                <CustomIcon
                                                    className={cn(classes.actionBtn, classes.actionBtn1)}
                                                    icon={icons.chart}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                            arrow
                                            placement="bottom"
                                            title={intl.formatMessage({ id: "download.report" })}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                                    handleDownloadReport(ev, data.id)
                                                }
                                            >
                                                <CustomIcon
                                                    className={cn(classes.actionBtn, classes.actionBtn1)}
                                                    icon={icons.downloadFile}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}

                                {/* Commented until we work on this */}
                                {/* <Tooltip
                                    arrow
                                    placement="bottom-start"
                                    title={intl.formatMessage({ id: "pace.portal.tranferProfile.tooltip" })}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                            handleTransferProfile(ev, data.id)
                                        }
                                    >
                                        <CustomIcon
                                            className={cn(classes.actionBtn, classes.actionBtn2)}
                                            icon={icons.cardWithArrows}
                                        />
                                    </IconButton>
                                </Tooltip> */}
                            </>
                        )}
                        <IconButton
                            size="small"
                            onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                handleGetInfo(ev, data.id)
                            }
                        >
                            <InfoIcon className={cn(classes.actionBtn, classes.actionBtn2)} />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow className={classes.expandableRow}>
                <TableCell colSpan={7}>
                    <Collapse in={shouldExpand} timeout="auto" unmountOnExit>
                        <Box display="flex" alignItems="center" py={1.2} px={0}>
                            <Box display="flex" alignItems="center" mr={2}>
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark", p: 0, mr: 1 }}
                                    onClick={onClickRestoreIcon}
                                    disabled={!data.personId}
                                >
                                    <RestoreIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark", p: 0 }}
                                    aria-label="linkedin button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!hasLinkedin}
                                    href={hasLinkedin ? `https://www.linkedin.com/in/${data.linkedinId}/` : "#"}
                                    onClick={handleStopPropagation}
                                >
                                    <CustomIcon icon={icons.linkedin} sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Box>
                            {data.email && (
                                <Box display="flex" alignItems="center" mr={2} color="text.secondary">
                                    <Typography mr={1}>
                                        {intl.formatMessage({
                                            id: "hubManagement.client.userTable.email",
                                        })}
                                        :
                                    </Typography>
                                    <Typography>{data.email}</Typography>
                                </Box>
                            )}
                            {hasLinkedin && (
                                <Box display="flex" alignItems="center" color="text.secondary">
                                    <Typography mr={1}>
                                        {intl.formatMessage({
                                            id: "hubManagement.client.userTable.linkedInID",
                                        })}
                                        :
                                    </Typography>
                                    <Typography>{data.linkedinId}</Typography>
                                </Box>
                            )}
                            <IconButton
                                className={classes.toggleCloseBtn}
                                onClick={(event) => handleToggleExpand(event)}
                            >
                                <CustomIcon icon={icons.close} />
                            </IconButton>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            {toggleShowWorkHistory ? (
                <ClickAwayListener onClickAway={onCloseWH}>
                    <PersonWorkHistory
                        role={""}
                        personId={data.personId}
                        anchorEl={WHAnchorEl}
                        onClose={onCloseWH}
                        open={toggleShowWorkHistory}
                    />
                </ClickAwayListener>
            ) : null}
        </>
    );
};

export default UserTableRow;
